import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { icd10Api } from "../../../shared/api/encounters";
import { useDebounce } from "../../../shared/hooks/useDebounce";
import type { Icd10Entry } from "../../../shared/types/encounter";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface Props {
  onPick: (entry: Icd10Entry) => void;
  placeholder?: string;
}

/**
 * Type-ahead picker over the ICD-10 catalog. Free-text fallback: typing a
 * non-matching string + Enter passes a fake entry with `icd10Code=null` so
 * doctors aren't blocked by the catalog.
 */
export function Icd10Combobox({ onPick, placeholder }: Props) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const debounced = useDebounce(q, 180);

  const results = useQuery({
    queryKey: ["icd10-search", debounced],
    queryFn: () => icd10Api.search(debounced, 12),
    enabled: open && debounced.length >= 1,
  });

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const items = results.data ?? [];

  function commit(entry: Icd10Entry) {
    onPick(entry);
    setQ("");
    setOpen(false);
  }

  /**
   * Free-text fallback — but a typed ICD-10 CODE must never become the diagnosis NAME.
   *
   * #19912 / #19913: the search is debounced 180ms, so a doctor who types a full code and hits
   * Enter straight away lands here with an empty result list. The old code then stored
   * `{code: "", labelVi: "J02.9"}` — the code in the label field and no code at all, which the
   * backend persists as the `"NA"` sentinel. The printed encounter summary showed an empty
   * ICD-10 column with "J02.9" under "Chẩn đoán", and the A5 prescription showed "J02.9 (NA)".
   * Resolve the text against the catalogue by code first; only genuinely unknown text stays
   * free-text.
   */
  async function commitFreeText() {
    const text = q.trim();
    if (!text) return;
    const exact = await icd10Api.getByCode(text).catch(() => null);
    if (exact) {
      commit(exact);
      return;
    }
    commit({ code: "", labelVi: text, labelEn: "" });
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(items.length, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (active < items.length && items[active]) commit(items[active]);
      else void commitFreeText();
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div className="relative" ref={wrap}>
      <Input
        type="search"
        value={q}
        placeholder={placeholder ?? "Tìm ICD-10 hoặc nhập chẩn đoán tự do…"}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onKeyDown={onKey}
        aria-expanded={open}
        role="combobox"
        aria-autocomplete="list"
      />
      {open && debounced.length >= 1 && (
        <div
          className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md"
          role="listbox"
        >
          {results.isLoading && (
            <div className="px-3 py-2 text-sm text-muted-foreground">Đang tìm…</div>
          )}
          {!results.isLoading && items.length === 0 && (
            <div
              className="cursor-pointer px-3 py-2 hover:bg-accent hover:text-accent-foreground"
              onMouseDown={(e) => {
                e.preventDefault();
                void commitFreeText();
              }}
            >
              <div className="text-sm font-medium">
                + Thêm "{q.trim()}" (chẩn đoán tự do)
              </div>
              <div className="text-xs text-muted-foreground">
                Không tìm thấy ICD-10 — bấm Enter để thêm dạng free-text.
              </div>
            </div>
          )}
          {items.map((entry, i) => (
            <div
              key={entry.code}
              role="option"
              aria-selected={i === active}
              className={cn(
                "cursor-pointer px-3 py-2 hover:bg-accent hover:text-accent-foreground",
                i === active && "bg-accent text-accent-foreground"
              )}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(entry);
              }}
            >
              <div className="text-sm font-medium">
                <span className="mr-2 font-mono text-muted-foreground">{entry.code}</span>
                {entry.labelVi}
              </div>
              <div className="text-xs text-muted-foreground">{entry.labelEn}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
